import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import type { Tables, TablesInsert } from '@/lib/supabase'
import { formatCST } from '@/lib/time'
import { AnimeMembershipDrawer } from '@/components/AnimeMembershipDrawer'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/anime-series')({
  component: AnimeSeriesPage,
})

type Row = Tables<'anime_series'>

// 手机卡片列表分页（桌面表格仍是 50 条/页）
const MOBILE_PAGE_SIZE = 20

interface FormValues {
  title: string
  title_original: string | null
  description: string | null
  cover_url: string | null
  bangumi_id: number | null
}

function AnimeSeriesPage() {
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const [editing, setEditing] = useState<Row | 'new' | null>(null)
  const [managing, setManaging] = useState<Row | null>(null)
  const [search, setSearch] = useState('')
  const isMobile = useIsMobile()
  const [mobilePage, setMobilePage] = useState(1)

  useEffect(() => {
    setMobilePage(1)
  }, [search])

  const listQuery = useQuery({
    queryKey: ['anime_series', 'list', search],
    queryFn: async () => {
      let q = supabase
        .from('anime_series')
        .select('*')
        .order('updated_at', { ascending: false, nullsFirst: false })
      if (search.trim()) {
        q = q.ilike('title', `%${search.trim()}%`)
      }
      const { data, error } = await q
      if (error) throw error
      return data
    },
  })

  const createMutation = useMutation({
    mutationFn: async (input: TablesInsert<'anime_series'>) => {
      const { error } = await supabase.from('anime_series').insert(input)
      if (error) throw error
    },
    onSuccess: () => {
      message.success('已新增')
      qc.invalidateQueries({ queryKey: ['anime_series'] })
      setEditing(null)
    },
    onError: (e: Error) => message.error(e.message || '新增失败'),
  })

  const updateMutation = useMutation({
    mutationFn: async (input: { id: number; patch: FormValues }) => {
      const { error } = await supabase
        .from('anime_series')
        .update(input.patch)
        .eq('id', input.id)
      if (error) throw error
    },
    onSuccess: () => {
      message.success('已保存')
      qc.invalidateQueries({ queryKey: ['anime_series'] })
      setEditing(null)
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('anime_series')
        .delete()
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      message.success('已删除')
      qc.invalidateQueries({ queryKey: ['anime_series'] })
    },
    onError: (e: Error) => message.error(e.message || '删除失败'),
  })

  function handleDelete(row: Row) {
    modal.confirm({
      title: `删除「${row.title}」？`,
      content: '此操作不可撤销。如有番剧引用该系列，删除会失败。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => deleteMutation.mutateAsync(row.id),
    })
  }

  const columns: ColumnsType<Row> = [
    {
      title: '封面',
      dataIndex: 'cover_url',
      width: 64,
      render: (url: string | null) =>
        url ? (
          <Image
            src={url}
            alt=""
            width={40}
            height={56}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={{ mask: false }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 56,
              borderRadius: 4,
              background: 'var(--ant-color-fill-tertiary)',
            }}
          />
        ),
    },
    {
      title: '标题',
      render: (_: unknown, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.title}</div>
          {row.title_original ? (
            <div
              style={{
                fontSize: 12,
                color: 'var(--ant-color-text-tertiary)',
                marginTop: 2,
              }}
            >
              {row.title_original}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: '简介',
      dataIndex: 'description',
      render: (v: string | null) =>
        v ? (
          <Typography.Paragraph
            ellipsis={{ rows: 2, tooltip: v }}
            style={{ marginBottom: 0, maxWidth: 320 }}
          >
            {v}
          </Typography.Paragraph>
        ) : (
          <span style={{ color: 'var(--ant-color-text-quaternary)' }}>—</span>
        ),
    },
    {
      title: 'Bangumi',
      dataIndex: 'bangumi_id',
      width: 100,
      render: (v: number | null) =>
        v ? (
          <Typography.Link
            href={`https://bgm.tv/subject/${v}`}
            target="_blank"
            rel="noreferrer"
          >
            #{v}
          </Typography.Link>
        ) : (
          '—'
        ),
    },
    {
      title: '更新',
      dataIndex: 'updated_at',
      width: 140,
      render: (t: string | null) =>
        t ? (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12 }}
            title={formatCST(t)}
          >
            {formatCST(t, 'YYYY-MM-DD HH:mm')}
          </Typography.Text>
        ) : (
          '—'
        ),
    },
    {
      title: '操作',
      width: 200,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={() => setManaging(row)}
          >
            番剧
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditing(row)}
          >
            编辑
          </Button>
          <Popconfirm
            title="删除？"
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
            onConfirm={() => handleDelete(row)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const searchBox = (
    <Input.Search
      placeholder="搜索标题"
      allowClear
      style={{ width: 220 }}
      onSearch={setSearch}
    />
  )

  return (
    <>
      <Card
        title="番剧系列"
        extra={
          <Space>
            {/* 手机上搜索框挪进卡片正文，页头只留紧凑操作 */}
            {isMobile ? null : searchBox}
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => listQuery.refetch()}
                loading={listQuery.isFetching && !listQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing('new')}
            >
              新增
            </Button>
          </Space>
        }
      >
        {isMobile ? (
          <div className="anime-series-mobile-search">{searchBox}</div>
        ) : null}
        <Table<Row>
          className="admin-table-wrap"
          rowKey="id"
          size="middle"
          loading={listQuery.isLoading}
          columns={columns}
          dataSource={listQuery.data ?? []}
          pagination={{
            pageSize: 50,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 800 }}
        />
        {isMobile ? (
          <AdminMobileList<Row>
            rows={(listQuery.data ?? []).slice(
              (mobilePage - 1) * MOBILE_PAGE_SIZE,
              mobilePage * MOBILE_PAGE_SIZE,
            )}
            rowKey={(row) => row.id}
            loading={listQuery.isLoading}
            page={mobilePage}
            pageSize={MOBILE_PAGE_SIZE}
            total={listQuery.data?.length ?? 0}
            onPageChange={setMobilePage}
            renderItem={(row) => (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                aria-label={`编辑系列「${row.title}」`}
                onClick={() => setEditing(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEditing(row)
                  }
                }}
              >
                <div className="admin-mobile-record__lead">
                  {row.cover_url ? (
                    <img
                      className="anime-series-mobile-cover"
                      src={row.cover_url}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <div className="anime-series-mobile-cover" />
                  )}
                  <div className="anime-series-mobile-main">
                    <div className="admin-mobile-record__title">
                      {row.title}
                    </div>
                    {row.title_original ? (
                      <div className="anime-series-mobile-sub">
                        {row.title_original}
                      </div>
                    ) : null}
                    {row.description ? (
                      <Typography.Paragraph
                        type="secondary"
                        ellipsis={{ rows: 2 }}
                        style={{ marginBottom: 0, fontSize: 12 }}
                      >
                        {row.description}
                      </Typography.Paragraph>
                    ) : null}
                    <div className="admin-mobile-record__meta">
                      {row.bangumi_id ? (
                        <span>Bangumi #{row.bangumi_id}</span>
                      ) : null}
                      {row.updated_at ? (
                        <span title={formatCST(row.updated_at)}>
                          更新 {formatCST(row.updated_at, 'YYYY-MM-DD')}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <AdminRecordActions
                  actions={[
                    {
                      key: 'delete',
                      label: '删除',
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => handleDelete(row),
                    },
                    {
                      key: 'edit',
                      label: '编辑',
                      icon: <EditOutlined />,
                      primary: true,
                      onClick: () => setEditing(row),
                    },
                    {
                      key: 'animes',
                      label: '番剧',
                      icon: <UnorderedListOutlined />,
                      primary: true,
                      onClick: () => setManaging(row),
                    },
                  ]}
                />
              </article>
            )}
          />
        ) : null}
      </Card>

      <EditModal
        editing={editing}
        onClose={() => setEditing(null)}
        onSubmit={(values) => {
          if (editing === 'new') {
            createMutation.mutate({
              title: values.title,
              title_original: values.title_original,
              description: values.description,
              cover_url: values.cover_url,
              bangumi_id: values.bangumi_id,
            })
          } else if (editing) {
            updateMutation.mutate({ id: editing.id, patch: values })
          }
        }}
        saving={createMutation.isPending || updateMutation.isPending}
      />

      <AnimeMembershipDrawer
        open={!!managing}
        targetId={managing?.id ?? null}
        targetTitle={managing?.title ?? ''}
        field="series_id"
        label="系列"
        onClose={() => setManaging(null)}
      />
    </>
  )
}

function EditModal({
  editing,
  onClose,
  onSubmit,
  saving,
}: {
  editing: Row | 'new' | null
  onClose: () => void
  onSubmit: (v: FormValues) => void
  saving: boolean
}) {
  const [form] = Form.useForm<FormValues>()
  const [fullscreen, setFullscreen] = useState(false)
  const isNew = editing === 'new'
  const row = editing && editing !== 'new' ? editing : null

  useEffect(() => {
    if (!editing) {
      form.resetFields()
      setFullscreen(false)
      return
    }
    if (isNew) {
      form.setFieldsValue({
        title: '',
        title_original: null,
        description: null,
        cover_url: null,
        bangumi_id: null,
      })
    } else if (row) {
      form.setFieldsValue({
        title: row.title,
        title_original: row.title_original,
        description: row.description,
        cover_url: row.cover_url,
        bangumi_id: row.bangumi_id,
      })
    }
  }, [editing, isNew, row, form])

  return (
    <Modal
      open={!!editing}
      title={
        <Space
          align="center"
          style={{ width: '100%', justifyContent: 'space-between' }}
        >
          <span>{isNew ? '新增系列' : `编辑 · ${row?.title ?? ''}`}</span>
          <Button
            type="text"
            size="small"
            icon={
              fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
            }
            onClick={() => setFullscreen((v) => !v)}
            aria-label={fullscreen ? '退出全屏' : '全屏编辑'}
            style={{ marginRight: 24 }}
          />
        </Space>
      }
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={saving}
      okText="保存"
      cancelText="取消"
      width={fullscreen ? '100%' : 560}
      className={fullscreen ? 'app-fullscreen-modal' : undefined}
      destroyOnHidden
    >
      <Form<FormValues>
        form={form}
        // Modal 内容晚于 effect 挂载，setFieldsValue 首次打开会落空；用 initialValues 兜底回显
        initialValues={
          row
            ? {
                title: row.title,
                title_original: row.title_original,
                description: row.description,
                cover_url: row.cover_url,
                bangumi_id: row.bangumi_id,
              }
            : undefined
        }
        layout="vertical"
        requiredMark={false}
        onFinish={onSubmit}
        preserve={false}
      >
        <Form.Item
          label="标题"
          name="title"
          rules={[
            { required: true, message: '请输入标题' },
            { max: 200, message: '最长 200 字' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="原标题" name="title_original">
          <Input placeholder="日文 / 英文原名等" />
        </Form.Item>
        <Form.Item label="封面 URL" name="cover_url">
          <Input placeholder="https://..." />
        </Form.Item>
        <Form.Item
          label="Bangumi ID"
          name="bangumi_id"
          extra="可选。链接到 bgm.tv/subject/{id}"
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="简介" name="description">
          <Input.TextArea rows={4} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
